/**
 * TypeScript mirror of the backend clean-citation contract.
 *
 * Source of truth: `backend/app/modules/citations/schemas/artifact.py`
 * (see `ideas/plan_clean_citation_architecture.md` §4).
 *
 * Citations are first-class typed atoms: a value and its source stay together
 * (`CitedValue`). The UI renders the atom directly — no render-time relinking.
 * `display` is exactly what the UI shows and must never be recomputed here.
 *
 * Validate untrusted payloads with `parseCitedTableArtifact` before use
 * (frontend "validate at boundary" rule). We hand-roll a small validator here
 * rather than use Zod: Zod is not used elsewhere in this app and its v4 type
 * entry does not resolve under the project's `moduleResolution: "node"`.
 */

export type SourceKind =
	| "excel"
	| "pdf"
	| "web"
	| "record"
	| "comms"
	| "derived";

export type VerificationStatus =
	| "verified"
	| "unverified"
	| "missing"
	| "ambiguous"
	| "invalid"
	| "not_applicable";

export type CellStatus =
	| "pending"
	| "filled"
	| "missing"
	| "ambiguous"
	| "invalid"
	| "not_applicable"
	// Source was checked and the value is genuinely absent — renders as a clean
	// blank, distinct from "not_applicable" (slot does not apply to this entity).
	| "not_present"
	| "error";

export type CoverageStatus = "covered" | "partial" | "failed";

// ---------------------------------------------------------------------------
// Source refs (discriminated on `kind`)
// ---------------------------------------------------------------------------

export interface ExcelSourceRef {
	kind: "excel";
	file_id: number;
	filename?: string | null;
	sheet: string;
	cell?: string | null;
	range?: string | null;
	sheet_key?: string | null;
	source_key?: string | null;
	workbook_sha256?: string | null;
	raw_value?: unknown;
	display_value?: string | null;
	number_format?: string | null;
	formula?: string | null;
	/** Row/metric label of this cell (e.g. "Payroll & Benefits"), when resolved. */
	label?: string | null;
}

export interface PdfSourceRef {
	kind: "pdf";
	file_id: number;
	filename?: string | null;
	page: number;
	source_key?: string | null;
	excerpt?: string | null;
	bbox?: number[] | null;
	text_sha256?: string | null;
}

export interface WebSourceRef {
	kind: "web";
	url: string;
	title?: string | null;
	publisher?: string | null;
	retrieved_at: string;
	source_key?: string | null;
	snippet?: string | null;
	provider_source_id?: string | null;
}

export interface RecordSourceRef {
	kind: "record";
	source_system: string;
	object_type: string;
	record_id: string;
	field: string;
	/** Snapshot the field was read from — the version anchor for re-query. */
	snapshot_sha256: string;
	version?: string | null;
	source_key?: string | null;
	/** Human name of the row (e.g. "Cedar Ridge Revolver"). Display only. */
	record_label?: string | null;
	/** The snapshot's last sync, ISO-8601 — the chip's as-of stamp. */
	as_of?: string | null;
}

export interface CommsSourceRef {
	kind: "comms";
	source_system: "email" | "slack";
	thread_id: string;
	message_id: string;
	sender: string;
	sent_at: string;
	excerpt: string;
	/** Content anchor used by the comms verifier (excerpt-hash match). */
	excerpt_hash: string;
	source_key?: string | null;
	/** Email subject or Slack channel name — what the thread is CALLED.
	 * `thread_id` is an ingestion slug and must never be the visible label. */
	thread_label?: string | null;
	/** 1-based position of the message in the projection ("msg 6"). */
	message_ordinal?: number | null;
	/** Projection document (FileAttachment PK) for click-through. */
	file_id?: number | null;
}

export type DerivedOperation = "sum" | "formula" | "arithmetic" | "other";

export interface DerivedSourceRef {
	kind: "derived";
	operation: DerivedOperation;
	expression?: string | null;
	formula?: string | null;
	/** The exact component sources composing this value. */
	inputs: SourceRef[];
	source_key?: string | null;
}

export type SourceRef =
	| ExcelSourceRef
	| PdfSourceRef
	| WebSourceRef
	| RecordSourceRef
	| CommsSourceRef
	| DerivedSourceRef;

// ---------------------------------------------------------------------------
// Verification
// ---------------------------------------------------------------------------

export interface VerificationIssue {
	code: string;
	message: string;
	severity: "info" | "warning" | "error";
	source_index?: number | null;
}

export interface CitationVerification {
	status: VerificationStatus;
	verifier: string;
	checked_at: string;
	issues: VerificationIssue[];
}

// ---------------------------------------------------------------------------
// Slot context (exact-vs-semantic axis)
// ---------------------------------------------------------------------------

/**
 * Semantic slot-correctness — ORTHOGONAL to `CitationVerification`.
 * Verification answers "does the cited cell hold this value?"; this answers
 * "is that cell the right source for the requested metric/period?".
 *
 * - `confirmed`  — the source row label matches the requested metric (exact).
 * - `mapped`     — a value was matched by meaning; the source row label differs
 *                  from the requested label (semantic).
 * - `unconfirmed`— the context label/header could not be located to check.
 * - `contradicted`— a parseable period/header clearly conflicts (review flag).
 */
export type SlotContextStatus =
	| "confirmed"
	| "mapped"
	| "unconfirmed"
	| "contradicted";

export interface SlotContext {
	status: SlotContextStatus;
	source_row_label?: string | null;
	source_period_label?: string | null;
	note?: string | null;
}

// ---------------------------------------------------------------------------
// Cited value
// ---------------------------------------------------------------------------

export interface CitedValue {
	value: unknown;
	display: string;
	metric_key: string;
	metric_label?: string | null;
	period_key?: string | null;
	unit?: string | null;
	scale?: string | null;
	sources: SourceRef[];
	verification: CitationVerification;
	// Exact-vs-semantic axis. `status === "mapped"` means the value was matched by
	// meaning (source row label differs from the requested metric). Rendered as a
	// per-cell marker; never affects `verification.status`.
	slot_context?: SlotContext | null;
	extractor?: string | null;
	confidence?: "high" | "medium" | "low" | null;
	// Dependency-graph identity (Stage 3b/4). `value_id` is this node's stable id;
	// `input_value_ids` are the atomic leaf nodes a derived value was computed
	// from, so the UI can render an input trace (each leaf -> its source cell +
	// verification). Empty for atomic leaves.
	value_id?: string | null;
	input_value_ids?: string[];
	// Durable evidence-lineage handle (§12/§17). When present, the lineage-
	// expansion UI can fetch the full cross-run derivation DAG (evidence_atom_edges)
	// from the lineage endpoint. Absent for values not yet backed by a durable atom
	// — the UI then falls back to the in-payload `sources` derivation tree.
	atom_id?: string | null;
}

// ---------------------------------------------------------------------------
// Slot, cell, layout, coverage
// ---------------------------------------------------------------------------

export interface TemplateSlot {
	slot_id: string;
	metric_key: string;
	metric_label: string;
	period_key?: string | null;
	file_id?: number | null;
	row_key: string;
	column_key: string;
	required: boolean;
	expected_source_kinds: SourceKind[];
}

export interface ExtractionCell {
	slot_id: string;
	status: CellStatus;
	value?: CitedValue | null;
	attempts: number;
	last_error?: string | null;
}

export interface TableColumn {
	column_key: string;
	label: string;
	kind: "dimension" | "value" | "metadata";
}

export interface TableRow {
	row_key: string;
	label: string;
	/**
	 * Optional section heading. Consecutive rows sharing a section render under
	 * one section header row; null/undefined means ungrouped.
	 */
	section?: string | null;
	/** column_key -> slot_id */
	cells: Record<string, string>;
	/**
	 * column_key -> display text for dimension columns. Supports tables with
	 * more than one dimension (e.g. File + Metric). Falls back to `label` when a
	 * dimension column is absent here.
	 */
	dimension_values?: Record<string, string>;
}

export interface CoverageSummary {
	total_required: number;
	verified: number;
	unverified: number;
	missing: number;
	ambiguous: number;
	invalid: number;
	not_applicable: number;
	not_present?: number;
	status: CoverageStatus;
}

export interface CitedTableArtifact {
	schema_version: "citation_artifact.v1";
	artifact_id: string;
	template_id: string;
	title?: string | null;
	columns: TableColumn[];
	rows: TableRow[];
	slots: Record<string, TemplateSlot>;
	cells: Record<string, ExtractionCell>;
	coverage: CoverageSummary;
	warnings: string[];
	created_by?: string | null;
	/** Follow-up lineage: first prior artifact this was merged from. */
	parent_artifact_id?: string | null;
	/** All direct prior artifacts this was merged from. */
	parent_artifact_ids?: string[];
}

// ---------------------------------------------------------------------------
// Boundary validation (dependency-free)
// ---------------------------------------------------------------------------

export const CITATION_ARTIFACT_SCHEMA_VERSION = "citation_artifact.v1" as const;

export class CitedTableArtifactParseError extends Error {}

function isRecord(value: unknown): value is Record<string, unknown> {
	return typeof value === "object" && value !== null && !Array.isArray(value);
}

const COVERAGE_STATUSES: readonly CoverageStatus[] = [
	"covered",
	"partial",
	"failed",
];

const COMMS_SOURCE_SYSTEMS: readonly string[] = ["email", "slack"];
const DERIVED_OPERATIONS: readonly string[] = [
	"sum",
	"formula",
	"arithmetic",
	"other",
];

function fail(message: string): never {
	throw new CitedTableArtifactParseError(message);
}

/**
 * Validate one nested source ref against the backend Pydantic constraints so a
 * malformed locator is rejected at the boundary — the same way the backend
 * discriminated `SourceRef` union rejects it (BE/FE rejection parity is the
 * point of this mirror).
 *
 * Each KNOWN variant is checked to exactly its backend-required fields, no more
 * and no less: only fields the Pydantic model requires (no default) are
 * enforced, so a payload the backend would accept always parses here too. An
 * UNKNOWN `kind` passes — a source variant added to the backend before the
 * frontend still degrades gracefully (rendered as a neutral chip; see
 * `EvidencePeek/sourceKind`) rather than failing the whole artifact.
 */
export function validateSourceRef(source: unknown, path: string): void {
	if (!isRecord(source)) fail(`${path} must be an object`);
	const kind = source.kind;
	if (typeof kind !== "string") fail(`${path}.kind must be a string`);

	const requireStrings = (keys: readonly string[]): void => {
		for (const key of keys) {
			if (typeof source[key] !== "string") {
				fail(`${path}.${key} must be a string`);
			}
		}
	};

	switch (kind) {
		case "excel":
			if (typeof source.file_id !== "number") {
				fail(`${path}.file_id must be a number`);
			}
			requireStrings(["sheet"]);
			// Mirror the backend `_cell_xor_range` invariant.
			if (source.cell != null && source.range != null) {
				fail(`${path} must set at most one of 'cell' or 'range'`);
			}
			break;
		case "pdf":
			if (typeof source.file_id !== "number") {
				fail(`${path}.file_id must be a number`);
			}
			if (
				typeof source.page !== "number" ||
				!Number.isInteger(source.page) ||
				source.page < 1
			) {
				fail(`${path}.page must be a 1-indexed integer`);
			}
			break;
		case "web":
			requireStrings(["url", "retrieved_at"]);
			break;
		case "record":
			requireStrings([
				"source_system",
				"object_type",
				"record_id",
				"field",
				"snapshot_sha256",
			]);
			if (source.version != null && typeof source.version !== "string") {
				fail(`${path}.version must be a string`);
			}
			break;
		case "comms":
			if (
				typeof source.source_system !== "string" ||
				!COMMS_SOURCE_SYSTEMS.includes(source.source_system)
			) {
				fail(
					`${path}.source_system must be one of ${COMMS_SOURCE_SYSTEMS.join(" | ")}`,
				);
			}
			requireStrings([
				"thread_id",
				"message_id",
				"sender",
				"sent_at",
				"excerpt",
				"excerpt_hash",
			]);
			break;
		case "derived":
			// `operation` and `inputs` both carry backend defaults, so they are
			// optional here; when present they must be well-formed, and inputs are
			// validated recursively (a bad nested locator fails the parent).
			if (
				source.operation !== undefined &&
				(typeof source.operation !== "string" ||
					!DERIVED_OPERATIONS.includes(source.operation))
			) {
				fail(
					`${path}.operation must be one of ${DERIVED_OPERATIONS.join(" | ")}`,
				);
			}
			if (source.inputs !== undefined) {
				if (!Array.isArray(source.inputs)) {
					fail(`${path}.inputs must be an array`);
				}
				source.inputs.forEach((inp, i) =>
					validateSourceRef(inp, `${path}.inputs[${i}]`),
				);
			}
			break;
		default:
			// Unknown variant: forward-compatible pass (graceful degradation).
			break;
	}
}

/**
 * Structurally validate an untrusted payload into a typed artifact.
 *
 * Validates everything the renderer reads — schema version, the layout arrays
 * and their entry shapes, the slot/cell maps, coverage status, and warnings —
 * so a malformed backend payload is rejected at the boundary rather than
 * crashing or misrendering. It also validates each cell value's nested source
 * refs (`validateSourceRef`) so a malformed locator is rejected here exactly as
 * the backend discriminated union rejects it. It does not re-derive coverage or
 * re-check other nested fields; the backend Pydantic contract stays
 * authoritative for those (and already enforces ledger-derived coverage).
 * Throws on mismatch.
 */
export function parseCitedTableArtifact(input: unknown): CitedTableArtifact {
	if (!isRecord(input)) fail("artifact must be an object");
	if (input.schema_version !== CITATION_ARTIFACT_SCHEMA_VERSION) {
		fail(`unsupported schema_version: ${String(input.schema_version)}`);
	}
	for (const key of ["artifact_id", "template_id"] as const) {
		if (typeof input[key] !== "string") fail(`missing string field: ${key}`);
	}

	if (!Array.isArray(input.columns)) fail("columns must be an array");
	input.columns.forEach((col, i) => {
		if (!isRecord(col) || typeof col.column_key !== "string") {
			fail(`columns[${i}] is missing column_key`);
		}
		if (typeof col.label !== "string") fail(`columns[${i}] is missing label`);
	});

	if (!Array.isArray(input.rows)) fail("rows must be an array");
	input.rows.forEach((row, i) => {
		if (!isRecord(row) || typeof row.row_key !== "string") {
			fail(`rows[${i}] is missing row_key`);
		}
		if (typeof row.label !== "string") fail(`rows[${i}] is missing label`);
		if (
			row.section !== undefined &&
			row.section !== null &&
			typeof row.section !== "string"
		) {
			fail(`rows[${i}].section must be a string or null`);
		}
		if (!isRecord(row.cells)) fail(`rows[${i}].cells must be an object`);
		if (
			row.dimension_values !== undefined &&
			!isRecord(row.dimension_values)
		) {
			fail(`rows[${i}].dimension_values must be an object`);
		}
	});

	if (!isRecord(input.slots)) fail("slots must be an object");
	if (!isRecord(input.cells)) fail("cells must be an object");

	// Descend into each filled cell's value to reject malformed source refs — the
	// only nested fields the renderer dereferences per-variant. Other cell/value
	// fields stay backend-authoritative (see the note above).
	for (const [slotId, cell] of Object.entries(input.cells)) {
		if (!isRecord(cell)) continue;
		const value = cell.value;
		if (!isRecord(value)) continue;
		const sources = value.sources;
		if (sources === undefined) continue;
		if (!Array.isArray(sources)) {
			fail(`cells.${slotId}.value.sources must be an array`);
		}
		sources.forEach((src, i) =>
			validateSourceRef(src, `cells.${slotId}.value.sources[${i}]`),
		);
	}

	if (!isRecord(input.coverage)) fail("coverage must be an object");
	if (!COVERAGE_STATUSES.includes(input.coverage.status as CoverageStatus)) {
		fail(`invalid coverage.status: ${String(input.coverage.status)}`);
	}

	if (input.warnings !== undefined && !Array.isArray(input.warnings)) {
		fail("warnings must be an array");
	}

	// Normalize the one field the renderer iterates unconditionally.
	const normalized = {
		...input,
		warnings: Array.isArray(input.warnings) ? input.warnings : [],
	};
	return normalized as unknown as CitedTableArtifact;
}

export interface SafeParseResult {
	success: boolean;
	data?: CitedTableArtifact;
	error?: Error;
}

/** Non-throwing variant. */
export function safeParseCitedTableArtifact(input: unknown): SafeParseResult {
	try {
		return { success: true, data: parseCitedTableArtifact(input) };
	} catch (error) {
		return {
			success: false,
			error: error instanceof Error ? error : new Error(String(error)),
		};
	}
}
